/**
 * MSPM DISO tenant user management — aligns with backend CMDISOTenantUserManagement read/invite DTOs (camelCase JSON).
 */

export interface CmdisoTenantUserAreaAssignmentRead {
  areasOfResponsibilityId: string;
  areasOfResponsibilityName: string;
  isManager: boolean;
}

/** AspNet role option for MSPM DISO invite (matches CMDISOTenantUserAssignableRoleReadDto). */
export interface CmdisoAssignableRoleRead {
  id: string;
  name: string;
}

export interface CmdisoTenantUserRead {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string;
  /** CMUesrStatus enum underlying value from backend. */
  userStatus: number;
  /** Human-readable lifecycle label (e.g. Pending Registration). */
  lifecycleDisplayStatus: string;
  areaAssignments: CmdisoTenantUserAreaAssignmentRead[];
  /** DISO AspNet role names (sorted). */
  aspNetRoleNames?: string[];
  /** CMContact.Title (string). */
  title?: string | null;
  /** AspNetUsers.DateLastLogin (ISO). */
  dateLastLogin?: string | null;
  /** When true, delete is hidden; owner is listed first and highlighted; edit is allowed without role change. */
  isDisoOwner?: boolean;
  /** When true, local B2C password reset is not available (Task 2544). */
  isFederated?: boolean;
  /**
   * True when B2C directory account exists; false when orphaned; null/undefined when check unavailable (Task 2544).
   */
  hasDirectoryAccount?: boolean | null;
}

export interface CmdisoMasterDepartmentCatalogItemRead {
  id: string;
  name: string;
  description: string | null;
}

export interface CmdisoTenantUserManagementReadModel {
  disoTenantId: string;
  /** CMDISO account BrandName for display (optional). */
  disoTenantDisplayName?: string | null;
  users: CmdisoTenantUserRead[];
  masterDepartments: CmdisoMasterDepartmentCatalogItemRead[];
  /** Roles for invite dropdown (Owner excluded server-side). */
  assignableRoles?: CmdisoAssignableRoleRead[];
  /** Config-driven country labels (MspmDisoCountryList:CountryNames). */
  countryNames?: string[];
}

/** AOR drift row (CMDISOTenantAorSyncScenarioDto). */
export interface CmdisoTenantAorSyncScenario {
  scenarioCode: string;
  description: string;
  userId?: string | null;
  areasOfResponsibilityId?: string | null;
}

/** GET aor-sync-status (CMDISOTenantAorSyncStatusDto). */
export interface CmdisoTenantAorSyncStatus {
  hasDrift: boolean;
  driftCount: number;
  scenarios: CmdisoTenantAorSyncScenario[];
}

/** POST aor-sync-apply body (CMDISOTenantAorSyncApplyRequestDto). Direction: 0 = CmdisoToProfile, 1 = ProfileToCmdiso. */
export interface CmdisoTenantAorSyncApplyRequest {
  direction: number;
  scopedUserId?: string | null;
}

export interface CmdisoTenantUserMspmDepartmentInvite {
  areasOfResponsibilityId: string;
  isManager: boolean;
}

export interface CmdisoTenantUserMspmInviteRequest {
  email: string;
  userName: string;
  lName?: string | null;
  roleId: string;
  /**
   * User preferred language (en|he|ar). Null/omitted = inherit tenant default (Task 2531 T181).
   * Do not default to en on the client.
   */
  preferredLanguageCode?: string | null;
  title?: number | null;
  phone?: string | null;
  departments: CmdisoTenantUserMspmDepartmentInvite[];
}

/** POST {disoTenantId}/invite response (T-2571). False: the user exists but the invitation email was not sent. */
export interface CmdisoTenantUserInviteResult {
  invitationEmailSent: boolean;
}

/** CMUesrStatus values with an open invitation (T-2571): RegistrationSentToUser and InvitationEmailPending. */
export const CMDISO_USER_STATUS_REGISTRATION_SENT = 0;
export const CMDISO_USER_STATUS_INVITATION_EMAIL_PENDING = 6;

/**
 * Task 2014 — MSPM create user with password (BE Task 2013).
 * Same shape as invite plus password; never log or persist password client-side beyond the request.
 */
export interface CmdisoTenantUserMspmPasswordCreateRequest {
  email: string;
  userName: string;
  lName?: string | null;
  roleId: string;
  /**
   * User preferred language (en|he|ar). Null/omitted = inherit tenant default (Task 2531 T181).
   * Do not default to en on the client.
   */
  preferredLanguageCode?: string | null;
  title?: number | null;
  phone?: string | null;
  password: string;
  departments: CmdisoTenantUserMspmDepartmentInvite[];
}

/** Task 2544 — MSPM admin reset password for existing DISO tenant user (confirm is FE-only). */
export interface CmdisoTenantUserMspmPasswordResetRequest {
  password: string;
}

/** Task 2544 — MSPM admin recreate missing B2C directory account (confirm is FE-only). */
export interface CmdisoTenantUserMspmRecreateDirectoryRequest {
  password: string;
}

/** Phase 3: PUT body for department-managers (one manager user per listed department). */
export interface CmdisoTenantDepartmentManagerAssignment {
  areasOfResponsibilityId: string;
  managerUserId: string;
}

export interface CmdisoTenantDepartmentManagersSaveRequest {
  departments: CmdisoTenantDepartmentManagerAssignment[];
}

/** MSPM unified edit — department row (CMDISOTenantUserEditDepartmentRowDto). */
export interface CmdisoTenantUserEditDepartmentRow {
  areasOfResponsibilityId: string;
  name: string;
  assigned: boolean;
  isManager: boolean;
}

/** Merged GET edit-model (CMDISOTenantUserEditModelDto). */
export interface CmdisoTenantUserEditModel {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  photo?: string | null;
  /** SAS URL from MSPM when photo is a private blob path (contact_icon/…); use for &lt;img src&gt;. */
  photoDisplayUrl?: string | null;
  countryName?: string | null;
  cityName?: string | null;
  currentRoleId?: string | null;
  aspNetRoleNames?: string[];
  departments: CmdisoTenantUserEditDepartmentRow[];
  /** Non-blocking when DISO vs Profile email differs (v1 email read-only). */
  profileSyncWarning?: string | null;
  isDisoAccountOwner: boolean;
  userStatusDescription?: string | null;
  usrDirectoryObjID?: string | null;
  isFederated?: boolean;
  dateStatusUtc?: string | null;
  dateLastLoginUtc?: string | null;
  needPasswordUpdate?: boolean;
}

/** PUT body (CMDISOTenantUserMspmUpdateRequestDto). */
export interface CmdisoTenantUserMspmUpdateDepartment {
  areasOfResponsibilityId: string;
  assigned: boolean;
  isManager: boolean;
}

export interface CmdisoTenantUserMspmUpdateRequest {
  firstName: string;
  lastName: string;
  title?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  countryName?: string | null;
  cityName?: string | null;
  roleId?: string | null;
  departments: CmdisoTenantUserMspmUpdateDepartment[];
}
